import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, CardBody, CardHeader, Table, Input, Button, Row, Col,
  Spinner, Alert, Badge, ButtonGroup, UncontrolledTooltip
} from 'reactstrap';
import Select from 'react-select';
import { comptabiliteActions } from '../../sagas/comptabiliteSlice';
import {
  FaSearch, FaDownload, FaPrint, FaCalendarAlt, FaFileAlt,
  FaCoins, FaUsers, FaTruck, FaBuilding, FaRedo, FaInfoCircle
} from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';

const BRAND = {
  primary: '#0d6efd',
  ink: '#0F172A',
  subtle: '#6B7280',
  line: '#E5E7EB',
  success: '#198754',
  warning: '#FFC107',
  info: '#0dcaf0',
  danger: '#dc3545',
};

const ReleveCompte = () => {
  const dispatch = useDispatch();
  const {
    releveCompte,
    comptes,
    clientAccounts,
    supplierAccounts,
    loading = {},
    error
  } = useSelector((s) => s.comptabilite);

  const [accountType, setAccountType] = useState('general'); // 'general' | 'clients' | 'suppliers'
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    selectedCompte: null,
    dateDebut: '',
    dateFin: ''
  });

  // Initial loads
  useEffect(() => {
    dispatch(comptabiliteActions.getComptes({ search: '' }));
    dispatch(comptabiliteActions.getClientAccounts());
    dispatch(comptabiliteActions.getSupplierAccounts());
  }, [dispatch]);

  // Reset selected account when switching type
  useEffect(() => {
    setFilters((p) => ({ ...p, selectedCompte: null }));
  }, [accountType]);

  // Debounced search (comptes généraux)
  useEffect(() => {
    const t = setTimeout(() => {
      dispatch(comptabiliteActions.getComptes({ search: searchTerm || '' }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, dispatch]);

  // Helpers
  const getCurrentAccounts = () => {
    if (accountType === 'clients') return clientAccounts || [];
    if (accountType === 'suppliers') return supplierAccounts || [];
    return comptes || [];
  };

  const compteOptions = (getCurrentAccounts()).map((c) => ({
    value: c.numero,
    label: `${c.numero} — ${c.intitule}`,
    typeCompte: c.typeCompte,
    soldeActuel: c.soldeActuel,
    compteCollectif: c.compteCollectif,
    codeTiers: c.codeTiers,
    idTiers: c.idTiers,
  }));

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 42,
      borderColor: state.isFocused ? BRAND.primary : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13,110,253,.15)' : 'none',
      '&:hover': { borderColor: BRAND.primary }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? BRAND.primary : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      padding: '8px 12px',
    })
  };

  const formatOptionLabel = ({ label, typeCompte, soldeActuel, compteCollectif, codeTiers }) => (
    <div className="d-flex justify-content-between align-items-center">
      <div>
        <span>{label}</span>
        {compteCollectif && (
          <div className="small text-muted">
            <i className="mdi mdi-account-group me-1"></i>
            Collectif: {compteCollectif} {codeTiers && `| Code: ${codeTiers}`}
          </div>
        )}
      </div>
      <div className="d-flex align-items-center">
        <Badge color={
          typeCompte === 'ACTIF' ? 'success' :
          typeCompte === 'PASSIF' ? 'info' :
          typeCompte === 'CHARGE' ? 'warning' : 'primary'
        } className="me-2">{typeCompte}</Badge>
        <small className="text-muted">{formatPrice(soldeActuel)}</small>
      </div>
    </div>
  );

  const handleFilterChange = (field, value) => {
    setFilters((p) => ({ ...p, [field]: value }));
  };

  const handleCompteChange = (opt) => {
    handleFilterChange('selectedCompte', opt);
  };

  const currentLoading = useMemo(() => {
    if (accountType === 'clients') return loading.clientAccounts;
    if (accountType === 'suppliers') return loading.supplierAccounts;
    return loading.comptes;
  }, [loading, accountType]);

  const handleSearch = () => {
    if (!filters.selectedCompte) {
      alert('Veuillez sélectionner un compte');
      return;
    }
    dispatch(comptabiliteActions.getReleveCompte({
      numeroCompte: filters.selectedCompte.value,
      dateDebut: filters.dateDebut || null,
      dateFin: filters.dateFin || null
    }));
  };

  // Quick date presets
  const todayISO = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const last30 = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);

  const applyPreset = (key) => {
    if (key === 'mois') {
      setFilters((p) => ({ ...p, dateDebut: firstDayOfMonth, dateFin: todayISO }));
    } else if (key === '30j') {
      setFilters((p) => ({ ...p, dateDebut: last30, dateFin: todayISO }));
    } else if (key === 'auj') {
      setFilters((p) => ({ ...p, dateDebut: todayISO, dateFin: todayISO }));
    }
  };

  // Derived numbers
  const totals = useMemo(() => {
    if (!releveCompte?.ecritures?.length) {
      return { debit: 0, credit: 0, soldeInitial: releveCompte?.soldeInitial || 0, soldeActuel: releveCompte?.soldeActuel || 0 };
    }
    const debit = releveCompte.ecritures.reduce((s, e) => s + Number(e.montantDebit || 0), 0);
    const credit = releveCompte.ecritures.reduce((s, e) => s + Number(e.montantCredit || 0), 0);
    return {
      debit,
      credit,
      soldeInitial: Number(releveCompte.soldeInitial || 0),
      soldeActuel: Number(releveCompte.soldeActuel || 0),
    };
  }, [releveCompte]);

  // Running balance per row
  const rowsWithRunning = useMemo(() => {
    if (!releveCompte?.ecritures) return [];
    let running = Number(releveCompte?.soldeInitial || 0);
    return releveCompte.ecritures.map((e) => {
      running = running + Number(e.montantDebit || 0) - Number(e.montantCredit || 0);
      return { ...e, running };
    });
  }, [releveCompte]);

  const onReset = () => {
    setAccountType('general');
    setSearchTerm('');
    setFilters({ selectedCompte: null, dateDebut: '', dateFin: '' });
  };

  const exportToPDF = () => console.log('TODO: export pdf');
  const exportToExcel = () => console.log('TODO: export xlsx');

  return (
    <div className="page-content">
      {/* Top Bar */}
      <div className="toolbar card shadow-sm">
        <div className="toolbar__left">
          <div className="title">
            <FaFileAlt className="me-2 text-primary" />
            <div>
              <h4 className="mb-0">Relevé de Compte</h4>
              <small className="text-muted">Consultez les mouvements et le solde de vos comptes</small>
            </div>
          </div>
        </div>
        <div className="toolbar__right">
          <Button color="outline-secondary" onClick={exportToPDF} disabled={!releveCompte}>
            <FaPrint className="me-1" /> PDF
          </Button>
          <Button color="outline-success" onClick={exportToExcel} disabled={!releveCompte}>
            <FaDownload className="me-1" /> Excel
          </Button>
          <Button id="resetBtn" color="light" onClick={onReset}>
            <FaRedo className="me-1" /> Réinitialiser
          </Button>
          <UncontrolledTooltip target="resetBtn">Effacer les filtres</UncontrolledTooltip>
        </div>
      </div>

      <Row className="g-3">
        {/* Filters */}
        <Col lg="4">
          <Card className="shadow-sm">
            <CardHeader className="bg-light py-2">
              <div className="d-flex align-items-center">
                <FaSearch className="me-2 text-primary" />
                <h6 className="mb-0">Critères de recherche</h6>
              </div>
            </CardHeader>
            <CardBody className="pb-3">
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <FaBuilding className="me-1" /> Type de compte
                </label>
                <ButtonGroup className="w-100">
                  <Button
                    color={accountType === 'general' ? 'primary' : 'outline-primary'}
                    onClick={() => setAccountType('general')}
                    className="btn-sm"
                  >
                    <FaCoins className="me-1" /> Généraux
                  </Button>
                  <Button
                    color={accountType === 'clients' ? 'success' : 'outline-success'}
                    onClick={() => setAccountType('clients')}
                    className="btn-sm"
                  >
                    <FaUsers className="me-1" /> Clients
                  </Button>
                  <Button
                    color={accountType === 'suppliers' ? 'warning' : 'outline-warning'}
                    onClick={() => setAccountType('suppliers')}
                    className="btn-sm"
                  >
                    <FaTruck className="me-1" /> Fournisseurs
                  </Button>
                </ButtonGroup>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <FaCoins className="me-1" /> Compte *
                </label>
                <Select
                  options={compteOptions}
                  value={filters.selectedCompte}
                  onChange={handleCompteChange}
                  placeholder={`Sélectionner un compte ${accountType === 'clients' ? 'client' : accountType === 'suppliers' ? 'fournisseur' : 'général'}...`}
                  isLoading={!!currentLoading}
                  isSearchable
                  onInputChange={setSearchTerm}
                  formatOptionLabel={formatOptionLabel}
                  styles={customSelectStyles}
                  noOptionsMessage={() => "Aucun compte trouvé"}
                  loadingMessage={() => "Chargement..."}
                />
              </div>

              <Row className="g-2 align-items-end mb-2">
                <Col md="6">
                  <label className="form-label fw-semibold">
                    <FaCalendarAlt className="me-1" /> Date début
                  </label>
                  <Input
                    type="date"
                    value={filters.dateDebut}
                    onChange={(e) => handleFilterChange('dateDebut', e.target.value)}
                  />
                </Col>
                <Col md="6">
                  <label className="form-label fw-semibold">
                    <FaCalendarAlt className="me-1" /> Date fin
                  </label>
                  <Input
                    type="date"
                    value={filters.dateFin}
                    onChange={(e) => handleFilterChange('dateFin', e.target.value)}
                  />
                </Col>
                <Col xs="12">
                  <div className="chips">
                    <button className="chip" onClick={() => applyPreset('mois')}>Ce mois</button>
                    <button className="chip" onClick={() => applyPreset('30j')}>30 derniers jours</button>
                    <button className="chip" onClick={() => applyPreset('auj')}>Aujourd’hui</button>
                  </div>
                </Col>
              </Row>

              <Button
                color="primary"
                className="w-100"
                disabled={loading.releveCompte || !filters.selectedCompte}
                onClick={handleSearch}
              >
                {loading.releveCompte ? <><Spinner size="sm" className="me-2" /> Recherche…</> : <><FaSearch className="me-2" />Rechercher</>}
              </Button>
            </CardBody>
          </Card>

          {/* Account info & KPIs */}
          {releveCompte && (
            <Card className="shadow-sm mt-3">
              <CardHeader className="bg-primary text-white py-2">
                <div className="d-flex align-items-center">
                  <FaCoins className="me-2" />
                  <h6 className="mb-0">Informations du compte</h6>
                </div>
              </CardHeader>
              <CardBody>
                <div className="mb-3">
                  <small className="text-muted d-block">Numéro</small>
                  <div className="fw-bold text-primary">{releveCompte.numeroCompte}</div>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block">Intitulé</small>
                  <div>{releveCompte.intituleCompte}</div>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block">Type</small>
                  <Badge color={
                    releveCompte.typeCompte === 'ACTIF' ? 'success' :
                    releveCompte.typeCompte === 'PASSIF' ? 'info' : 'warning'
                  }>
                    {releveCompte.typeCompte}
                  </Badge>
                </div>

                <Row className="g-2">
                  <Col xs="6">
                    <div className="kpi kpi-soft">
                      <small className="text-muted d-block">Solde initial</small>
                      <div className="h6 mb-0">{formatPrice(totals.soldeInitial)}</div>
                    </div>
                  </Col>
                  <Col xs="6">
                    <div className="kpi">
                      <small className="text-muted d-block">Solde actuel</small>
                      <div className={`h6 mb-0 ${totals.soldeActuel >= 0 ? 'text-success' : 'text-danger'}`}>
                        {formatPrice(totals.soldeActuel)}
                      </div>
                    </div>
                  </Col>
                  <Col xs="6">
                    <div className="kpi">
                      <small className="text-muted d-block">Total débit</small>
                      <div className="h6 mb-0 text-warning">{formatPrice(totals.debit)}</div>
                    </div>
                  </Col>
                  <Col xs="6">
                    <div className="kpi">
                      <small className="text-muted d-block">Total crédit</small>
                      <div className="h6 mb-0 text-info">{formatPrice(totals.credit)}</div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}
        </Col>

        {/* Ledger */}
        <Col lg="8">
          {error && (
            <Alert color="danger" className="shadow-sm">
              <FaInfoCircle className="me-2" /> {String(error)}
            </Alert>
          )}

          {loading.releveCompte && (
            <Card className="shadow-sm">
              <CardBody className="text-center py-5">
                <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                <div className="mt-3">
                  <h6>Chargement du relevé…</h6>
                  <p className="text-muted mb-0">Veuillez patienter</p>
                </div>
              </CardBody>
            </Card>
          )}

          {releveCompte && rowsWithRunning.length > 0 ? (
            <Card className="shadow-sm">
              <CardHeader className="bg-light py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <FaFileAlt className="me-2 text-primary" />
                    Écritures ({rowsWithRunning.length})
                  </h6>
                  <Badge color="primary" pill>{rowsWithRunning.length}</Badge>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                <div className="table-wrap">
                  <Table hover responsive className="mb-0 ledger-table">
                    <thead className="sticky">
                      <tr>
                        <th style={{ minWidth: 110 }}>Date</th>
                        <th style={{ minWidth: 120 }}>N° écriture</th>
                        <th style={{ minWidth: 220 }}>Libellé</th>
                        <th className="d-none d-md-table-cell" style={{ minWidth: 120 }}>Journal</th>
                        <th className="text-end" style={{ minWidth: 120 }}>Débit</th>
                        <th className="text-end" style={{ minWidth: 120 }}>Crédit</th>
                        <th className="text-end" style={{ minWidth: 140 }}>Solde courant</th>
                        <th className="d-none d-lg-table-cell" style={{ minWidth: 120 }}>Référence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowsWithRunning.map((e, i) => (
                        <tr key={i}>
                          <td>
                            <span className="badge-soft">
                              {new Date(e.dateEcriture).toLocaleDateString('fr-FR')}
                            </span>
                          </td>
                          <td className="fw-semibold">{e.numeroEcriture}</td>
                          <td className="text-wrap">{e.libelle}</td>
                          <td className="d-none d-md-table-cell">
                            <small className="text-muted">{e.journalLibelle}</small>
                          </td>
                          <td className="text-end">
                            {e.montantDebit
                              ? <span className="text-warning fw-semibold">{formatPrice(e.montantDebit)}</span>
                              : <span className="text-muted">-</span>}
                          </td>
                          <td className="text-end">
                            {e.montantCredit
                              ? <span className="text-info fw-semibold">{formatPrice(e.montantCredit)}</span>
                              : <span className="text-muted">-</span>}
                          </td>
                          <td className={`text-end fw-bold ${e.running >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatPrice(e.running)}
                          </td>
                          <td className="d-none d-lg-table-cell">
                            <small className="text-muted">{e.referenceDocument || '-'}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="sticky-footer">
                      <tr>
                        <td colSpan="4" className="text-end fw-bold">Totaux</td>
                        <td className="text-end fw-bold text-warning">{formatPrice(totals.debit)}</td>
                        <td className="text-end fw-bold text-info">{formatPrice(totals.credit)}</td>
                        <td className={`text-end fw-bold ${totals.soldeActuel >= 0 ? 'text-success' : 'text-danger'}`}>
                          {formatPrice(totals.soldeActuel)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </CardBody>
            </Card>
          ) : releveCompte && (
            <Card className="shadow-sm">
              <CardBody className="text-center py-5">
                <FaFileAlt size={42} className="text-muted mb-3" />
                <h6 className="mb-1">Aucune écriture trouvée</h6>
                <p className="text-muted mb-0">Aucune écriture comptable pour la période sélectionnée.</p>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>

      {/* Styles */}
      <style>{`
        .page-content {
          background: #f6f8fb;
          padding: 12px;
          min-height: 100vh;
        }
        .toolbar {
          padding: 12px 16px;
          border: 1px solid ${BRAND.line};
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: .5rem;
        }
        .title { display:flex; align-items:center; gap:12px; }
        .toolbar__right > * + * { margin-left: 8px; }

        .chips { display:flex; gap:6px; margin-top:6px; flex-wrap: wrap; }
        .chip {
          border: 1px solid ${BRAND.line};
          background: #fff;
          border-radius: 999px;
          padding: 4px 10px;
          font-size: .8rem;
          cursor: pointer;
        }
        .chip:hover { border-color: ${BRAND.primary}; }

        .kpi {
          border: 1px solid ${BRAND.line};
          border-radius: .5rem;
          padding: 8px 10px;
          background: #fff;
        }
        .kpi-soft {
          border: 1px dashed ${BRAND.line};
          background: #fcfcfd;
        }

        .table-wrap {
          max-height: calc(100vh - 320px);
          overflow: auto;
          border-top: 1px solid ${BRAND.line};
        }
        .ledger-table thead.sticky th {
          position: sticky; top: 0; z-index: 5;
          background: #1f2937; color: #fff; border: none;
        }
        .ledger-table tfoot.sticky-footer tr {
          position: sticky; bottom: 0; background: #fafafa;
          box-shadow: 0 -2px 0 rgba(0,0,0,.03);
        }
        .badge-soft {
          color: ${BRAND.subtle};
          background: rgba(108,117,125,.12);
          padding: 2px 8px;
          border-radius: 999px;
          font-size: .75rem;
        }

        /* Scrollbar */
        .table-wrap::-webkit-scrollbar { width: 8px; height: 8px; }
        .table-wrap::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
        .table-wrap::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        .table-wrap::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
      `}</style>
    </div>
  );
};

export default ReleveCompte;
